import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { MapContainer, TileLayer, Marker, Popup, CircleMarker, useMap } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import L from "leaflet";
import { useAuth, API } from "../auth";
import { toast } from "sonner";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Button } from "../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { MapPin, RefreshCw, Users, Calendar, Filter } from "lucide-react";
import { todayISO } from "../utils/date";

// Fix Leaflet default icon paths (Webpack strips them otherwise).
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
  iconUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
  shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
});

// Deterministic color per user_id — 12 vibrant hues cycling.
const USER_COLORS = [
  "#059669", "#dc2626", "#2563eb", "#d97706", "#7c3aed", "#db2777",
  "#0891b2", "#65a30d", "#ea580c", "#4f46e5", "#0284c7", "#a16207",
];
function colorForUser(userId) {
  if (!userId) return USER_COLORS[0];
  let h = 0;
  for (let i = 0; i < userId.length; i++) h = (h * 31 + userId.charCodeAt(i)) >>> 0;
  return USER_COLORS[h % USER_COLORS.length];
}

const PRESETS = [
  { key: "today", label: "Hoy" },
  { key: "7d", label: "7 días" },
  { key: "30d", label: "30 días" },
  { key: "custom", label: "Personalizado" },
];

function computeRange(preset) {
  const today = todayISO();
  if (preset === "today") return { from: today, to: today };
  if (preset === "7d") {
    const d = new Date(); d.setDate(d.getDate() - 6);
    return { from: d.toISOString().slice(0, 10), to: today };
  }
  if (preset === "30d") {
    const d = new Date(); d.setDate(d.getDate() - 29);
    return { from: d.toISOString().slice(0, 10), to: today };
  }
  return { from: "", to: "" };
}

export default function AdminMap() {
  const { authHeaders, user } = useAuth();
  const [preset, setPreset] = useState("7d");
  const [range, setRange] = useState(computeRange("7d"));
  const [filterUserId, setFilterUserId] = useState("all");
  const [filterChain, setFilterChain] = useState("");
  const [users, setUsers] = useState([]);
  const [points, setPoints] = useState([]);
  const [loading, setLoading] = useState(false);

  // Load users list once (admin can see all).
  useEffect(() => {
    axios.get(`${API}/users`, { headers: authHeaders() })
      .then((r) => setUsers(r.data || []))
      .catch(() => {});
  }, []);

  const fetchPoints = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (range.from) params.set("date_from", range.from);
      if (range.to) params.set("date_to", range.to);
      if (filterUserId && filterUserId !== "all") params.set("user_id", filterUserId);
      if (filterChain.trim()) params.set("chain", filterChain.trim());
      const { data } = await axios.get(`${API}/bandeos/map/points?${params.toString()}`, { headers: authHeaders() });
      setPoints(data || []);
    } catch (err) {
      toast.error("No se pudieron cargar los puntos");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPoints();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [range.from, range.to, filterUserId, filterChain]);

  const applyPreset = (p) => {
    setPreset(p);
    if (p !== "custom") setRange(computeRange(p));
  };

  const uniqueUsersInResults = useMemo(() => {
    const seen = new Map();
    for (const p of points) {
      if (!seen.has(p.user_id)) seen.set(p.user_id, p.user_name);
    }
    return Array.from(seen.entries()).map(([id, name]) => ({ id, name, color: colorForUser(id) }));
  }, [points]);

  const mapCenter = useMemo(() => {
    if (points.length === 0) return [8.9824, -79.5199]; // Panama City fallback
    const lat = points.reduce((s, p) => s + p.arrival_lat, 0) / points.length;
    const lng = points.reduce((s, p) => s + p.arrival_lng, 0) / points.length;
    return [lat, lng];
  }, [points]);

  return (
    <div className="p-5 md:p-8 lg:p-10 max-w-none" data-testid="admin-map-page">
      <div className="mb-6">
        <div className="kbd-label mb-2 text-emerald-700">/ Administración</div>
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3">
          <div>
            <h1 className="font-display text-4xl md:text-5xl font-bold tracking-tight text-slate-950 flex items-center gap-3">
              <MapPin className="w-8 h-8 text-emerald-700" />
              Mapa de visitas
            </h1>
            <p className="text-slate-500 mt-1 text-sm">
              Ubicaciones GPS capturadas al inicio de cada bandeo. Visible solo para administradores.
            </p>
          </div>
          <Button variant="outline" onClick={fetchPoints} disabled={loading} className="gap-2 self-start md:self-end" data-testid="refresh-btn">
            <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
            {loading ? "Actualizando…" : "Refrescar"}
          </Button>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white border border-slate-200 rounded-lg p-4 md:p-5 mb-4">
        <div className="flex items-center gap-2 mb-3">
          <Filter className="w-4 h-4 text-slate-400" />
          <div className="kbd-label">/ Filtros</div>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <div>
            <Label className="kbd-label flex items-center gap-1"><Calendar className="w-3 h-3" /> Periodo</Label>
            <Select value={preset} onValueChange={applyPreset}>
              <SelectTrigger className="mt-1.5" data-testid="filter-preset"><SelectValue /></SelectTrigger>
              <SelectContent>
                {PRESETS.map((p) => <SelectItem key={p.key} value={p.key}>{p.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="kbd-label">Desde</Label>
            <Input
              type="date"
              value={range.from}
              onChange={(e) => { setPreset("custom"); setRange((r) => ({ ...r, from: e.target.value })); }}
              className="mt-1.5"
              data-testid="filter-from"
            />
          </div>
          <div>
            <Label className="kbd-label">Hasta</Label>
            <Input
              type="date"
              value={range.to}
              onChange={(e) => { setPreset("custom"); setRange((r) => ({ ...r, to: e.target.value })); }}
              className="mt-1.5"
              data-testid="filter-to"
            />
          </div>
          <div>
            <Label className="kbd-label flex items-center gap-1"><Users className="w-3 h-3" /> Usuario</Label>
            <Select value={filterUserId} onValueChange={setFilterUserId}>
              <SelectTrigger className="mt-1.5" data-testid="filter-user"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos</SelectItem>
                {users.map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="col-span-2 md:col-span-4">
            <Label className="kbd-label">Cadena / Cliente</Label>
            <Input
              value={filterChain}
              onChange={(e) => setFilterChain(e.target.value)}
              placeholder="Filtra por cadena exacta (deja vacío para todas)"
              className="mt-1.5"
              data-testid="filter-chain"
            />
          </div>
        </div>
      </div>

      {/* Summary + legend */}
      <div className="flex flex-wrap items-center gap-3 mb-3">
        <div className="bg-white border border-slate-200 rounded-md px-3 py-1.5 text-sm" data-testid="points-count">
          <span className="kbd-label text-slate-500 mr-2">Visitas con GPS</span>
          <span className="font-display font-bold text-slate-950 tabular-nums">{points.length}</span>
        </div>
        {uniqueUsersInResults.length > 0 && (
          <div className="flex flex-wrap items-center gap-2 bg-white border border-slate-200 rounded-md px-3 py-1.5">
            <span className="kbd-label text-slate-500">Usuarios</span>
            {uniqueUsersInResults.slice(0, 12).map((u) => (
              <div key={u.id} className="flex items-center gap-1.5 text-xs">
                <span className="w-3 h-3 rounded-full border border-white shadow" style={{ backgroundColor: u.color }} />
                <span className="text-slate-700">{u.name}</span>
              </div>
            ))}
            {uniqueUsersInResults.length > 12 && <span className="text-xs text-slate-500">+{uniqueUsersInResults.length - 12}</span>}
          </div>
        )}
      </div>

      {/* Map */}
      <div className="bg-white border border-slate-200 rounded-lg overflow-hidden" style={{ height: "70vh", minHeight: 500 }} data-testid="map-container">
        <MapContainer center={mapCenter} zoom={points.length > 0 ? 11 : 8} className="w-full h-full" scrollWheelZoom>
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
          <FitBounds points={points} />
          {points.map((p) => {
            const color = colorForUser(p.user_id);
            return (
              <CircleMarker
                key={p.id}
                center={[p.arrival_lat, p.arrival_lng]}
                radius={9}
                pathOptions={{ color: "#ffffff", weight: 2, fillColor: color, fillOpacity: 0.9 }}
              >
                <Popup>
                  <div className="text-sm" data-testid={`pin-popup-${p.id}`}>
                    <div className="font-display font-bold text-slate-950">{p.chain}</div>
                    <div className="text-slate-700">{p.branch}</div>
                    <div className="text-slate-500 text-xs mt-1">
                      {p.fecha} · Llegada {p.arrival_time || "—"}
                      {p.departure_time ? ` · Salida ${p.departure_time}` : ""}
                    </div>
                    <div className="mt-2 pt-2 border-t border-slate-100 text-xs">
                      <div className="flex items-center gap-1.5">
                        <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: color }} />
                        <span className="text-slate-700">{p.user_name}</span>
                      </div>
                      {p.arrival_accuracy != null && (
                        <div className="text-slate-500 mt-1">Precisión GPS: ±{Math.round(p.arrival_accuracy)} m</div>
                      )}
                    </div>
                    <Link
                      to={`/bandeos/${p.id}`}
                      className="mt-3 inline-flex items-center gap-1 text-emerald-700 hover:text-emerald-900 font-medium text-xs underline underline-offset-2"
                      data-testid={`pin-detail-link-${p.id}`}
                    >
                      Ver reporte →
                    </Link>
                  </div>
                </Popup>
              </CircleMarker>
            );
          })}
        </MapContainer>
      </div>

      {points.length === 0 && !loading && (
        <div className="mt-4 bg-amber-50 border border-amber-200 rounded-lg p-4 text-sm text-amber-900" data-testid="empty-state">
          Sin visitas con GPS en el período/filtro seleccionado. Prueba con un rango más amplio o quita los filtros.
        </div>
      )}
    </div>
  );
}

// Auto-fit bounds to markers when points change.
function FitBounds({ points }) {
  const map = useMap();
  useEffect(() => {
    if (!points || points.length === 0) return;
    const latlngs = points.map((p) => [p.arrival_lat, p.arrival_lng]);
    if (latlngs.length === 1) {
      map.setView(latlngs[0], 15);
    } else {
      map.fitBounds(latlngs, { padding: [40, 40], maxZoom: 15 });
    }
  }, [points, map]);
  return null;
}
